import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';
import Typewriter from 'typewriter-effect/dist/core';
import gsap from "gsap";
import ScrollTrigger from 'gsap/ScrollTrigger';
import initSplitTitle from './utils/init-split-title';

const initExpandable = ({
  listSelector,
  itemSelector,
  textSelector,
  activeClass,
  arrowTL,
  arrowTR,
  arrowBL,
  arrowBR,
  arrowClass,
  mobileBreak = 960,
}) => {
  const list = document.querySelector(listSelector);
  if (!list) return;
 
  const items = Array.from(list.querySelectorAll(itemSelector));
  if (!items.length) return;
 
  const openItem = (item) => {
    item.classList.add(activeClass);
    const text = item.querySelector(textSelector);
    if (text) text.style.height = `${text.scrollHeight}px`;
  };
 
  const closeItem = (item) => {
    item.classList.remove(activeClass);
    const text = item.querySelector(textSelector);
    if (text) text.style.height = '0';
  };
 
  const arrowDefs = [
    { cls: `${arrowClass}--tl`, src: arrowTL },
    { cls: `${arrowClass}--tr`, src: arrowTR },
    { cls: `${arrowClass}--bl`, src: arrowBL },
    { cls: `${arrowClass}--br`, src: arrowBR },
  ];
 
  const arrows = arrowDefs.map(({ cls, src }) => {
    const img = document.createElement('img');
    img.className = `${arrowClass} ${cls}`;
    img.src = src;
    img.alt = '';
    list.appendChild(img);
    return img;
  });
 
  let activeItem       = null;
  let hideTimeout      = null;
  let arrowsVisible    = false;
  let rafId            = null;
  let lastTime         = 0;
  let snapOnFirstFrame = false;
 
  let curT = 0, curL = 0, curW = 0, curH = 0;
 
  const applyArrows = (t, l, w, h) => {
    const aw = arrows[0].offsetWidth;
    const ah = arrows[0].offsetHeight;

    arrows[0].style.top  = `${t}px`;
    arrows[0].style.left = `${l}px`;
    arrows[1].style.top  = `${t}px`;
    arrows[1].style.left = `${l + w - aw}px`;
    arrows[2].style.top  = `${t + h - ah}px`;
    arrows[2].style.left = `${l}px`;
    arrows[3].style.top  = `${t + h - ah}px`;
    arrows[3].style.left = `${l + w - aw}px`;
  };
 
  const animate = (time) => {
    if (!activeItem) { rafId = null; return; }
    
    const tT = activeItem.offsetTop;
    const tL = activeItem.offsetLeft;
    const tW = activeItem.offsetWidth;
    const tH = activeItem.offsetHeight;
 
    if (snapOnFirstFrame) {
      snapOnFirstFrame = false;
      curT = tT; curL = tL; curW = tW; curH = tH;
      applyArrows(curT, curL, curW, curH);
      arrows.forEach(a => { a.style.opacity = '1'; });
      arrowsVisible = true;
      lastTime = time;
      rafId = requestAnimationFrame(animate);
      return;
    }
 
    const dt = Math.min((time - lastTime) / 1000, 0.05);
    lastTime = time;
 
    const k = 1 - Math.exp(-14 * dt);
    curT += (tT - curT) * k;
    curL += (tL - curL) * k;
    curW += (tW - curW) * k;
    curH += (tH - curH) * k;
 
    applyArrows(curT, curL, curW, curH);
 
    rafId = requestAnimationFrame(animate);
  };
 
  const startRaf = () => {
    if (rafId) return;
    lastTime = performance.now();
    rafId = requestAnimationFrame(animate);
  };
 
  const stopRaf = () => {
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
  };
 
  const isMobile = () => window.innerWidth <= mobileBreak;

  const activateItem = (item) => {
    if (activeItem) closeItem(activeItem);
    activeItem = item;
    openItem(item);
    if (!arrowsVisible) snapOnFirstFrame = true;
    startRaf();
  };

  const deactivateItem = () => {
    if (!activeItem) return;
    stopRaf();
    snapOnFirstFrame = false;
    closeItem(activeItem);
    activeItem = null;
    arrows.forEach(a => { a.style.opacity = '0'; });
    arrowsVisible = false;
  };
 
  items.forEach((item) => {
    item.addEventListener('click', () => {
      if (!isMobile()) return;
 
      if (activeItem === item) {
        deactivateItem();
      } else {
        activateItem(item);
      }
    });
  });
 
  items.forEach((item) => {
    item.addEventListener('mouseenter', () => {
      if (isMobile()) return;
      clearTimeout(hideTimeout);
      if (activeItem === item) return;
      activateItem(item);
    });
 
    item.addEventListener('mouseleave', () => {
      if (isMobile()) return;
      hideTimeout = setTimeout(() => {
        if (activeItem === item) deactivateItem();
      }, 60);
    });
  });
  
  {
    const firstItem = items[0];
    const text = firstItem.querySelector(textSelector);

    const descendants = [firstItem, ...Array.from(firstItem.querySelectorAll('*'))];
    descendants.forEach(el => { el.style.transition = 'none'; });

    firstItem.classList.add(activeClass);
    if (text) text.style.height = `${text.scrollHeight}px`;

    void firstItem.offsetHeight;

    curT = firstItem.offsetTop;
    curL = firstItem.offsetLeft;
    curW = firstItem.offsetWidth;
    curH = firstItem.offsetHeight;
    applyArrows(curT, curL, curW, curH);
    activeItem = firstItem;
    arrowsVisible = true;

    requestAnimationFrame(() => {
      descendants.forEach(el => { el.style.transition = ''; });
      arrows.forEach(a => { a.style.opacity = '1'; });
    });

    startRaf();
  }

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (!activeItem || !arrowsVisible) return;

      const text = activeItem.querySelector(textSelector);
      if (text) text.style.height = `${text.scrollHeight}px`;

      curT = activeItem.offsetTop;
      curL = activeItem.offsetLeft;
      curW = activeItem.offsetWidth;
      curH = activeItem.offsetHeight;
      applyArrows(curT, curL, curW, curH);
    }, 100);
  });
};

const initProjectsAccordion = ({
  listSelector,
  itemSelector,
  boxSelector,
  nameSelector,
  activeClass,
  rootMargin = '0px 0px -50px 0px',
}) => {
  const list = document.querySelector(listSelector);
  if (!list) return;

  const items = Array.from(list.querySelectorAll(itemSelector));
  if (!items.length) return;

  const getCollapsedHeight = (item) => {
    const box  = item.querySelector(boxSelector);
    const name = item.querySelector(nameSelector);
    if (!box || !name) return 140;
    const style = getComputedStyle(box);
    const pt    = parseFloat(style.paddingTop)    || 40;
    const pb    = parseFloat(style.paddingBottom) || 40;
    return Math.round(pt + name.offsetHeight + pb);
  };
  
  const flipName = (name, fromRect) => {
    if (!name || !fromRect) return;
    const toRect = name.getBoundingClientRect();
    const dx = fromRect.left - toRect.left;
    const dy = fromRect.top  - toRect.top;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
    name.style.transition = 'none';
    name.style.transform  = `translate(${dx}px, ${dy}px)`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        name.style.transition = 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)';
        name.style.transform  = '';
      });
    });
  };

  const openItem = (item) => {
    if (item.classList.contains(activeClass)) return;
    const box  = item.querySelector(boxSelector);
    const name = item.querySelector(nameSelector);
    if (!box) return;

    const fromH    = parseFloat(box.style.height) || box.offsetHeight;
    const nameRect = name ? name.getBoundingClientRect() : null;

    item.classList.add(activeClass);

    const toH = box.scrollHeight;

    flipName(name, nameRect);

    box.style.transition = 'none';
    box.style.height     = fromH + 'px';
    void box.offsetHeight;
    box.style.transition = 'height 0.7s cubic-bezier(0.4, 0, 0.2, 1)';
    box.style.height     = toH + 'px';

    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      box.style.height     = '';
      box.style.transition = '';
      box.removeEventListener('transitionend', onEnd);
    };
    box.addEventListener('transitionend', onEnd);
  };

  const closeItem = (item) => {
    if (!item.classList.contains(activeClass)) return;
    const box  = item.querySelector(boxSelector);
    const name = item.querySelector(nameSelector);
    if (!box) return;

    const fromH    = box.offsetHeight;
    const nameRect = name ? name.getBoundingClientRect() : null;

    item.classList.remove(activeClass);

    const toH = getCollapsedHeight(item);

    flipName(name, nameRect);

    box.style.transition = 'none';
    box.style.height     = fromH + 'px';
    void box.offsetHeight;
    box.style.transition = 'height 0.7s cubic-bezier(0.4, 0, 0.2, 1)';
    box.style.height     = toH + 'px';
  };

  items.slice(1).forEach((item) => {
    const box = item.querySelector(boxSelector);
    if (box) box.style.height = getCollapsedHeight(item) + 'px';
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const item = entry.target;
        if (entry.isIntersecting) {
          openItem(item);
        } else if (entry.boundingClientRect.top > 0) {
          closeItem(item);
        }
      });
    },
    { rootMargin, threshold: 0 }
  );

  items.slice(1).forEach((item) => observer.observe(item));

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      items.forEach((item) => {
        const box = item.querySelector(boxSelector);
        if (!box) return;
        if (item.classList.contains(activeClass)) {
          box.style.height     = '';
          box.style.transition = '';
        } else {
          box.style.height = getCollapsedHeight(item) + 'px';
        }
      });
    }, 150);
  });
};

const initSwiper = (selector, options, viewportAutoplay) => {
  const element = document.querySelector(selector);
  if (!element) return;

  let swiperOptions = options;

  if (viewportAutoplay && options.autoplay) {
    swiperOptions = {
      ...options,
      autoplay: { ...options.autoplay, enabled: false },
    };
  }

  const swiper = new Swiper(element, swiperOptions);

  if (viewportAutoplay && options.autoplay) {
    const { sectionSelector = selector, threshold = 0.2 } = viewportAutoplay;
    const section = document.querySelector(sectionSelector);

    if (section) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              swiper.autoplay.start();
            } else {
              swiper.autoplay.stop();
            }
          });
        },
        { threshold }
      );

      observer.observe(section);
    }
  }

  return swiper;
};

const initWorkSliderReveal = ({
  sectionSelector,
  sliderSelector,
  visibleClass = 'is-visible',
  delay        = 4000,
  threshold    = 0.1,
  swiper
}) => {
  const section = document.querySelector(sectionSelector);
  const slider  = document.querySelector(sliderSelector);
  if (!section || !slider) return;

  let triggered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !triggered) {
          triggered = true;
          observer.disconnect();
          setTimeout(() => {
            slider.classList.add(visibleClass);

            swiper.params.autoplay.delay = 5500;
            swiper?.update();
            swiper?.autoplay?.start();
          }, delay);
        }
      });
    },
    { threshold }
  );

  observer.observe(section);
};

const initProjectsStackingCards = ({
  sectionSelector = '.projects',
  listSelector    = '.projects__list',
  itemSelector    = '.projects__item',
  scaleMin        = 0.92,
} = {}) => {
  const section = document.querySelector(sectionSelector);
  if (!section) return;

  const list = section.querySelector(listSelector);
  if (!list) return;

  const items = Array.from(list.querySelectorAll(itemSelector));
  if (items.length < 2) return;

  list.style.gap = '20px';

  items.forEach((item, i) => {
    item.style.position        = 'sticky';
    item.style.top             = '0';
    item.style.zIndex          = String(i + 1);
    item.style.willChange      = 'transform';
    item.style.transformOrigin = 'top center';
  });

  const update = () => {
    const vh = window.innerHeight;

    items.forEach((item, i) => {
      if (i === items.length - 1) {
        item.style.transform = '';
        return;
      }

      const nextTop = items[i + 1].getBoundingClientRect().top;

      const progress = Math.max(0, Math.min(1, 1 - nextTop / vh));

      item.style.transform = progress > 0
        ? `scale(${(1 - (1 - scaleMin) * progress).toFixed(4)})`
        : '';
    });
  };

  window.addEventListener('scroll', update, { passive: true });
  update();

  window.addEventListener('resize', () => requestAnimationFrame(update));
};

const initTyping = ({
  selector,
  threshold = 0.3,
  speed = 60,
  cursor = '|',
  startDelay = 0,
}) => {
  const element = document.querySelector(selector);
  if (!element) return;

  const text = element.innerHTML.trim();
  if (!text) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        element.classList.remove('typing-hidden');

        const typewriter = new Typewriter(element, {
          loop: false,
          delay: speed,
          cursor
        });

        typewriter
          .pauseFor(startDelay)
          .callFunction(() => {
            if (element.classList.contains('is-hidden-typing-wait')) {
              element.classList.add('typing-started');
            }
          })
          .typeString(text)
          .start();

        observer.disconnect();
      });
    },
    { threshold }
  );

  observer.observe(element);
};

document.addEventListener('DOMContentLoaded', () => {
  const assetsUrl = window.supernovaDevSettings.assetsUrl.replace(/\/$/, '');
  
  lottie.loadAnimation({
    container: document.getElementById('planet-animation'),
    renderer: 'svg',
    loop: true,
    autoplay: true,
    path: `${assetsUrl}/lottie/svg_planet_blink.json`,
  });

  initExpandable({
    listSelector : '.approach__list',
    itemSelector : '.approach__item',
    textSelector : '.approach__text',
    activeClass  : 'is-hovered',
    arrowTL      : `${assetsUrl}/images/decor/top-left-black-arrow.svg`,
    arrowTR      : `${assetsUrl}/images/decor/top-right-black-arrow.svg`,
    arrowBL      : `${assetsUrl}/images/decor/bottom-left-black-arrow.svg`,
    arrowBR      : `${assetsUrl}/images/decor/bottom-right-black-arrow.svg`,
    arrowClass   : 'approach-arrow',
  });

  initProjectsAccordion({
    listSelector : '.client-projects__list',
    itemSelector : '.client-projects__item',
    boxSelector  : '.client-projects__box',
    nameSelector : '.client-projects__name',
    activeClass  : 'is-open',
    rootMargin   : '0px 0px -200px 0px',
  });

  initSwiper('.services__swiper', {
    slidesPerView: 'auto',
    centeredSlides: true,
    loop: true,
    watchSlidesProgress: true,
    speed: 700,
    spaceBetween: 20,
    autoplay: {
      delay: 5500,
      disableOnInteraction: false,
    },
    navigation: {
      nextEl: '.services__arrow-next',
      prevEl: '.services__arrow-prev',
    },
    on: {
      sliderFirstMove() {
        const drug = document.querySelector('.services__drug');
    
        if (!drug || drug.classList.contains('is-hidden')) return;
    
        drug.classList.add('is-hidden');
      },
      setTranslate(swiper) {
        swiper.slides.forEach((slide) => {
          const progress = slide.progress;

          const p = Math.max(-2, Math.min(2, progress));
  
          const rotate = -p * 12;
          const translateY = Math.abs(p) * 85;
          const scale = 1 - Math.abs(p) * 0.08;
  
          slide.style.transform = `
            rotate(${rotate}deg)
            translateY(${translateY}px)
            scale(${scale})
          `;

          slide.style.zIndex = 100 - Math.abs(p) * 10;
        });
      },
  
      setTransition(swiper, duration) {
        swiper.slides.forEach((slide) => {
          slide.style.transitionDuration = `${duration}ms`;
        });
      },
    },
  }, {
    sectionSelector: '.services',
    threshold: 0.2,
  });

  const workSwiper = initSwiper('.work__swiper', {
    effect: 'creative',
    creativeEffect: {
      limitProgress: 2,
      prev: {
        translate: ['-100%', 0, 0],
        opacity: 0.3,
      },
      next: {
        translate: ['100%', 0, 0],
        opacity: 0.3,
      },
    },
    speed: 1800,
    watchSlidesProgress: true,

    touchRatio: 1.5,
    longSwipesRatio: 0.15,
    longSwipesMs: 100,
    threshold: 5,
    followFinger: true,
    slidesPerView: 1,
    spaceBetween: 15,
    loop: true,
    pagination: {
      el: '.work__pagination',
      clickable: true,
      renderBullet(index, className) {
        const number = String(index + 1).padStart(2, '0');
        return `<span class="${className}">${number}</span>`;
      },
    },
    on: {
      setTranslate(swiper) {
        swiper.slides.forEach((slide) => {
          const progress = Math.abs(slide.progress);
    
          slide.style.opacity = Math.max(0.1, 1 - progress * 1.3);
        });
      },
    
      setTransition(swiper, duration) {
        swiper.slides.forEach((slide) => {
          slide.style.transitionDuration = `${duration}ms`;
        });
      },
    },
  }, {
    sectionSelector: '.work',
    threshold: 0.2
  });

  initSwiper('.news__swiper', {
    slidesPerView: "auto",
    spaceBetween: 15,
  
    navigation: {
      nextEl: '.news__arrow-next',
      prevEl: '.news__arrow-prev',
    },
  
    pagination: {
      el: '.news__progress',
      type: 'progressbar',
    },
  
    breakpoints: {
      961: {
        slidesPerView: "auto",
        spaceBetween: 15,
        slidesOffsetBefore: 48,
        slidesOffsetAfter: 48,
      },
  
      481: {
        slidesPerView: 3,
        spaceBetween: 12,
        slidesOffsetBefore: 24,
        slidesOffsetAfter: 24,
      },
  
      0: {
        slidesPerView: "auto",
        spaceBetween: 12,
        slidesOffsetBefore: 12,
        slidesOffsetAfter: 12,
      },
    },
  });

  initWorkSliderReveal({
    sectionSelector : '.work',
    sliderSelector  : '.work__slider',
    visibleClass    : 'is-visible',
    delay           : 7000,
    threshold       : 0.1,
    swiper          : workSwiper,
  });

  initProjectsStackingCards({
    sectionSelector: '.projects',
    listSelector   : '.projects__list',
    itemSelector   : '.projects__item',
    scaleMin       : 0.92,
  });

  initTyping({
    selector: '.intro__text',
    threshold: 0.3,
    cursor: '▉'
  });

  initTyping({
    selector: '.team__text',
    threshold: 0.3,
    cursor: '▉',
    startDelay: 1000,
  });

  initTyping({
    selector: '.clients__text',
    threshold: 0.3,
    cursor: '▉',
    startDelay: 1000,
  });

  initSplitTitle();
});